/**
 * Plein écran du navigateur.
 *
 * Safari — macOS comme iPad — n'expose encore que les variantes préfixées
 * `webkit`, d'où la double lecture. Sur iPhone, aucune des deux n'existe pour
 * un élément qui n'est pas une vidéo : `isFullscreenSupported` répond alors
 * faux, ce qui permet de griser la commande plutôt que d'offrir un bouton sans
 * effet. L'application installée depuis Safari, elle, occupe déjà tout l'écran
 * — c'est ce que `canInstallForFullscreen` sert à proposer en échange.
 */

interface FullscreenDocument extends Document {
  webkitFullscreenEnabled?: boolean
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
}

interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void
}

function fullscreenDocument(): FullscreenDocument {
  return document as FullscreenDocument
}

/**
 * Le navigateur accepte-t-il de passer la page en plein écran ?
 *
 * Les deux moitiés comptent. Le drapeau seul ne suffit pas : Safari iPhone
 * réserve le plein écran aux vidéos et n'expose la méthode sur aucun autre
 * élément, alors qu'il connaît le vocabulaire de l'API. Et la méthode seule ne
 * suffit pas non plus : une page en cadre sans `allow="fullscreen"` la possède
 * mais se voit refuser la demande, ce que le drapeau annonce.
 */
export function isFullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false

  const doc = fullscreenDocument()
  const announced =
    doc.fullscreenEnabled === true || doc.webkitFullscreenEnabled === true

  const root = doc.documentElement as FullscreenElement
  const callable =
    typeof root.requestFullscreen === 'function' ||
    typeof root.webkitRequestFullscreen === 'function'

  return announced && callable
}

/** L'application tourne-t-elle depuis l'écran d'accueil ? */
function isHomeScreenApp(): boolean {
  return (
    matchMedia('(display-mode: standalone)').matches ||
    matchMedia('(display-mode: fullscreen)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  )
}

/**
 * L'installation sur l'écran d'accueil est-elle la voie de rechange ?
 *
 * Apple n'a jamais accordé le plein écran à autre chose qu'une vidéo sur
 * iPhone, et ne l'accorde toujours pas : le drapeau entrevu dans les bêtas
 * d'iOS 17.4 n'a jamais été activé par défaut. L'application posée sur l'écran
 * d'accueil, en revanche, s'ouvre sans barre d'adresse ni barre d'onglets —
 * le résultat recherché, par une autre porte. Encore faut-il le dire : rien
 * dans Safari ne laisse deviner que la page peut s'installer.
 *
 * La question ne se pose que sur un appareil d'Apple encore consulté dans un
 * onglet. L'iPad connaît `webkitRequestFullscreen` et n'arrive jamais jusqu'ici
 * ; ailleurs, un plein écran refusé tient à un cadre sans `allow` ou à un mode
 * kiosque, que nulle installation ne débloquerait.
 */
export function canInstallForFullscreen(): boolean {
  if (typeof document === 'undefined') return false
  if (isHomeScreenApp()) return false

  // Reconnaissance faite une seule fois, par le script en ligne d'`index.html`
  // — il lui faut être posée avant la première mise en page, pour les marges
  // basses. La relire évite d'entretenir deux tests de plateforme.
  return document.documentElement.hasAttribute('data-apple')
}

/** La page est-elle actuellement en plein écran ? */
export function isFullscreen(): boolean {
  if (typeof document === 'undefined') return false

  const doc = fullscreenDocument()
  return (doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null) !== null
}

/**
 * Entre en plein écran ou en sort.
 *
 * La demande doit partir d'un geste de l'utilisateur, sans quoi le navigateur
 * la rejette : elle est donc déclenchée par l'interrupteur des réglages, jamais
 * au chargement. Un refus n'est pas remonté — l'état qui fait foi est celui du
 * document, que `onFullscreenChange` fait suivre à l'interface.
 */
export async function setFullscreen(enabled: boolean): Promise<void> {
  if (enabled === isFullscreen()) return

  const doc = fullscreenDocument()

  try {
    if (enabled) {
      // `documentElement` et non le conteneur de la carte : le tiroir, les
      // dialogues et les réglages sont posés ailleurs dans la page, ils
      // disparaîtraient de l'écran.
      const root = doc.documentElement as FullscreenElement
      await (root.requestFullscreen?.() ?? root.webkitRequestFullscreen?.())
    } else {
      await (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.())
    }
  } catch {
    // Demande refusée (geste expiré, page en cadre, mode kiosque) : rien à
    // rattraper, l'interface se resynchronise sur l'état réel du document.
  }
}

/**
 * S'abonne aux entrées et sorties de plein écran.
 *
 * L'utilisateur peut en sortir par la touche Échap ou un geste du navigateur,
 * sans passer par la commande de l'application : l'interface ne peut pas se
 * contenter de mémoriser ce qu'elle a demandé.
 */
export function onFullscreenChange(listener: () => void): () => void {
  document.addEventListener('fullscreenchange', listener)
  document.addEventListener('webkitfullscreenchange', listener)

  return () => {
    document.removeEventListener('fullscreenchange', listener)
    document.removeEventListener('webkitfullscreenchange', listener)
  }
}
